<template>
  <main v-if="status">
    <p>{{ status }}</p>
    <loading :status="status" />
  </main>
  <main v-else>

    <h2>Home</h2>
    <RandomImage />

    <!-- User Messages Section -->
    <div v-if="notificationsStore.messages.length" class="user-messages">
      <h3>Messages</h3>
      <div
        v-for="msg in notificationsStore.messages"
        :key="msg.id"
        class="message-card"
      >
        <div class="message-content markdown-content" v-html="markdown(msg.content)"></div>
        <div class="message-footer">
          <small class="message-date">{{ msg.created_at }}</small>
          <button @click="notificationsStore.remove(msg.id)" class="delete-btn">
            Dismiss
          </button>
        </div>
      </div>
    </div>

    <!-- Instructions Section -->
    <div v-if="instructions" class="instructions-section">
      <div class="instructions-header">
        <h3>Instructions</h3>
        <button @click="toggleInstructions" class="toggle-btn">
          {{ isInstructionsExpanded ? 'Collapse' : 'Expand' }}
        </button>
      </div>
      <div
        v-html="markdown(instructions)"
        class="instructions-content markdown-content"
        :class="{ expanded: isInstructionsExpanded }"
      ></div>
    </div>


    <div class="stats-container">
      <div class="stats-card">
        <h3>Skills Overview</h3>
        <div class="stats-info">
          <p><strong>Total Skills:</strong> {{ skills.length }}</p>
          <p><strong>Total Weight:</strong> {{ totalWeight }}</p>
          <p><strong>Completed Projects:</strong> {{ completedProjectsCount }}</p>
        </div>
      </div>
    </div>


    <h3 class="main">My Skills</h3>
    <div class="skills-container">
      <div v-for="skill in skills" :key="skill.id" class="skill-item">
        <div class="skill-header" :style="{ backgroundColor: skill.color }">
          <span class="skill-name" :style="{ color: getTextColor(skill.color) }">{{
            skill.name
            }}</span>
          <span class="skill-weight" :style="{ color: getTextColor(skill.color) }">{{
            skill.weight
            }}</span>
        </div>
        <div class="skill-details">
          <p v-if="skill.icon"><img :src="skill.icon" alt="Skill icon" class="skill-icon" /></p>
          <p class="skill-description markdown-content" v-html="markdown(skill.description)"></p>
          <p class="skill-count">
            Gained in {{ skill.project_count }}
            {{ skill.project_count === 1 ? 'project' : 'projects' }}
          </p>
          <RouterLink :to="{ name: 'skill-detail', params: { id: skill.id } }" class="skill-link">
            Details
          </RouterLink>
        </div>
      </div>
    </div>

    <h3 class="main">Badges</h3>
    <div class="badges-container">
      <div v-for="badge in badges" :key="badge.id" class="badge-card">
        <div class="badge-header" :style="{ backgroundColor: badge.color }">
          <div class="badge-title">
            <img v-if="badge.icon" :src="badge.icon" alt="Badge icon" class="badge-icon" />
            <h4 :style="{ color: getTextColor(badge.color) }">{{ badge.name }}</h4>
          </div>
          <span class="badge-strategy" :style="{ color: getTextColor(badge.color) }">
            {{ badge.strategy }}
          </span>
        </div>

        <div class="badge-content">
          <!-- Badge Status -->
          <div class="badge-status" :class="{ achieved: isBadgeCompleted(badge) }">
            {{ isBadgeCompleted(badge) ? '✓ Badge Achieved!' : '⏳ Not Yet...' }}
          </div>

          <div class="badge-description markdown-content" v-html="markdown(badge.description)"></div>

          <!-- Conditions Overview -->
          <div class="conditions-summary">
            <h5>Conditions ({{ badge.strategy.toUpperCase() }} strategy):</h5>
            <div class="condition-badges">
              <span
                class="condition-badge"
                :class="{ met: getBadgeWeightProgress(badge).met }"
                v-if="badge.weight_limit > 0"
              >
                {{ getBadgeWeightProgress(badge).met ? '✓' : '✗' }} Weight Limit
              </span>
              <span
                class="condition-badge"
                :class="{ met: getBadgeSkillsProgress(badge).met }"
                v-if="badge.skills_count_limit > 0"
              >
                {{ getBadgeSkillsProgress(badge).met ? '✓' : '✗' }} Skills Count
              </span>
              <span
                class="condition-badge"
                :class="{ met: getBadgeSpecificSkillsProgress(badge).met }"
                v-if="badge.skills.length > 0"
              >
                {{ getBadgeSpecificSkillsProgress(badge).met ? '✓' : '✗' }} Specific Skills
              </span>
            </div>
          </div>

          <!-- Weight Progress -->
          <div class="progress-section" v-if="badge.weight_limit > 0">
            <h5>
              Weight Limit (Total project weight)
              <span class="condition-status" :class="{ met: getBadgeWeightProgress(badge).met }">
                {{ getBadgeWeightProgress(badge).met ? '✓ Met' : '✗ Not met' }}
              </span>
            </h5>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: getBadgeWeightProgress(badge).percentage + '%' }"
              ></div>
            </div>
            <div class="progress-text">
              {{ getBadgeWeightProgress(badge).current }} / {{ badge.weight_limit }} points
              ({{ getBadgeWeightProgress(badge).percentage }}%)
            </div>
          </div>

          <!-- Skills Count Progress -->
          <div class="progress-section" v-if="badge.skills_count_limit > 0">
            <h5>
              Skills Count Limit (Total unique skills)
              <span
                class="condition-status"
                :class="{ met: getBadgeSkillsProgress(badge).met }"
              >
                {{ getBadgeSkillsProgress(badge).met ? '✓ Met' : '✗ Not met' }}
              </span>
            </h5>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: getBadgeSkillsProgress(badge).percentage + '%' }"
              ></div>
            </div>
            <div class="progress-text">
              {{ getBadgeSkillsProgress(badge).completed }} /
              {{ badge.skills_count_limit }} skills ({{
                getBadgeSkillsProgress(badge).percentage
              }}%)
            </div>
          </div>

          <!-- Specific Skills Progress -->
          <div class="progress-section" v-if="badge.skills.length > 0">
            <h5>
              Specific Skills Required
              <span
                class="condition-status"
                :class="{ met: getBadgeSpecificSkillsProgress(badge).met }"
              >
                {{ getBadgeSpecificSkillsProgress(badge).met ? '✓ Met' : '✗ Not met' }}
              </span>
            </h5>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: getBadgeSpecificSkillsProgress(badge).percentage + '%' }"
              ></div>
            </div>
            <div class="progress-text">
              {{ getBadgeSpecificSkillsProgress(badge).completed }} /
              {{ getBadgeSpecificSkillsProgress(badge).total }} skills ({{
                getBadgeSpecificSkillsProgress(badge).percentage
              }}%)
            </div>

            <!-- All Required Skills -->
            <div class="required-skills" v-if="badge.skills.length > 0">
              <h5>Required Skills:</h5>
              <div class="skill-tags">
                <RouterLink
                  v-for="skill in getAllBadgeSkills(badge)"
                  :key="skill.id"
                  :to="{ name: 'skill-detail', params: { id: skill.id } }"
                  class="skill-tag-link"
                >
                  <span
                    class="skill-tag"
                    :class="{ obtained: skill.obtained }"
                    :style="{ backgroundColor: skill.color, color: getTextColor(skill.color) }"
                  >
                    {{ skill.obtained ? '✓' : '✗' }} {{ skill.name }} ({{ skill.weight }})
                  </span>
                </RouterLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </main>
</template>

<style scoped lang="scss">
@use 'sass:color';

h2 {
  margin-bottom: 2rem;
}

h3.main {
  margin-left: 1rem;
  text-decoration: underline;
  font-size: 2rem;
  color: $primari;
}

.stats-container {
  display: flex;
  flex-wrap: wrap;
  margin: 2rem 0;
  gap: 1rem;
}

.stats-card {
  background-color: rgba($primari, 0.07);
  border-radius: 8px;
  padding: 1.5rem;
  flex: 1;
  min-width: 250px;

  h3 {
    margin-top: 0;
    font-size: 1.5rem;
    color: $primari;
    margin-bottom: 1rem;
  }

  .stats-info {
    p {
      margin: 0.5rem 0;
      font-size: 1.1rem;
    }
  }
}

.skills-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 1.5rem 0 3rem 0;
}

.skill-item {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0px 0px 7px 7px $primari;
  }

  .skill-header {
    padding: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .skill-name {
      font-weight: bold;
      font-size: 1.2rem;
    }

    .skill-weight {
      font-weight: bold;
      border-radius: 50%;
      width: 2.5rem;
      height: 2.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid rgba(255, 255, 255, 0.7);
    }
  }

  .skill-details {
    padding: 1rem;

    .skill-icon {
      max-width: 48px;
      max-height: 48px;
      margin-bottom: 0.5rem;
    }

    .skill-description {
      margin-bottom: 1rem;
      font-size: 0.9rem;
      color: #555;
    }

    .skill-count {
      font-style: italic;
      font-size: 0.9rem;
      margin-bottom: 1rem;
    }

    .skill-link {
      display: inline-block;
      padding: 0.5rem 1rem;
      background-color: $primari;
      color: white;
      text-decoration: none;
      border-radius: 4px;
      transition: background-color 0.2s;

      &:hover {
        background-color: color.scale($primari, $lightness: -10%);
      }
    }
  }
}

.badges-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2rem;
  margin: 2rem 0;
}

.badge-card {
  border: 2px solid #ddd;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .badge-header {
    padding: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .badge-title {
      display: flex;
      align-items: center;
      gap: 1rem;

      h4 {
        margin: 0;
        font-size: 1.5rem;
        font-weight: bold;
      }

      .badge-icon {
        width: 48px;
        height: 48px;
        object-fit: contain;
      }
    }

    .badge-strategy {
      font-size: 0.9rem;
      font-weight: bold;
      text-transform: uppercase;
      padding: 0.3rem 0.8rem;
      background-color: rgba(255, 255, 255, 0.2);
      border-radius: 4px;
    }
  }

  .badge-content {
    padding: 1.5rem;
    background-color: white;

    .badge-description {
      margin-bottom: 1rem;
      font-size: 1rem;
      color: #555;
      line-height: 1.5;
    }

    .badge-status {
      margin-bottom: 1.5rem;
      padding: 1rem;
      background-color: #95a5a6;
      color: white;
      text-align: center;
      font-weight: bold;
      font-size: 1.1rem;
      border-radius: 6px;
      transition: all 0.3s ease;

      &.achieved {
        background-color: #27ae60;
      }
    }

    .conditions-summary {
      margin-bottom: 1.5rem;
      padding: 1rem;
      background-color: rgba($primari, 0.05);
      border-radius: 6px;

      h5 {
        margin: 0 0 0.8rem 0;
        font-size: 1rem;
        color: #333;
      }

      .condition-badges {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;

        .condition-badge {
          padding: 0.4rem 0.8rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 600;
          background-color: #e74c3c;
          color: white;

          &.met {
            background-color: #27ae60;
          }
        }
      }
    }

    .progress-section {
      margin-bottom: 1.5rem;

      h5 {
        margin: 0 0 0.5rem 0;
        font-size: 1rem;
        color: #333;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .condition-status {
          font-size: 0.85rem;
          font-weight: 600;
          padding: 0.2rem 0.6rem;
          border-radius: 3px;
          background-color: #e74c3c;
          color: white;

          &.met {
            background-color: #27ae60;
          }
        }
      }

      .progress-bar {
        height: 1.5rem;
        background-color: #e0e0e0;
        border-radius: 0.75rem;
        overflow: hidden;
        margin-bottom: 0.5rem;

        .progress-fill {
          height: 100%;
          background-color: #55aa00;
          border-radius: 0.75rem;
          transition: width 0.5s ease;
        }
      }

      .progress-text {
        font-size: 0.9rem;
        color: #666;
        text-align: right;
      }
    }

    .required-skills {
      margin-top: 1rem;

      h5 {
        margin: 0 0 0.8rem 0;
        font-size: 1rem;
        color: #333;
      }

      .skill-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;

        .skill-tag-link {
          text-decoration: none;
          transition: transform 0.2s ease;

          &:hover {
            transform: translateY(-2px);
          }

          .skill-tag {
            padding: 0.4rem 0.8rem;
            border-radius: 4px;
            font-size: 0.85rem;
            font-weight: 500;
            white-space: nowrap;
            display: inline-block;
            transition: all 0.2s ease;
            cursor: pointer;

            &.obtained {
              opacity: 0.6;
              border: 2px solid rgba(39, 174, 96, 0.5);
            }

            &:hover {
              opacity: 1;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
            }
          }
        }
      }
    }
  }
}

.user-messages {
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(#f0ad4e, 0.1);
  border: 2px solid #f0ad4e;
  border-radius: 8px;

  h3 {
    margin: 0 0 1rem 0;
    color: #8a6d3b;
    font-size: 1.3rem;
  }

  .message-card {
    background: white;
    border-radius: 6px;
    padding: 1rem;
    margin-bottom: 0.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &:last-child {
      margin-bottom: 0;
    }

    .message-content {
      margin: 0 0 0.5rem 0;
      font-size: 1rem;
      color: #333;
    }

    .message-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .message-date {
        color: #888;
        font-size: 0.85rem;
      }

      .delete-btn {
        padding: 0.3rem 0.8rem;
        background: #e74c3c;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.85rem;
        transition: background 0.2s;

        &:hover {
          background: #c0392b;
        }
      }
    }
  }
}

.instructions-section {
  margin-bottom: 2rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  background: white;

  .instructions-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: $primari;
    color: white;

    h3 {
      margin: 0;
      font-size: 1.2rem;
    }

    .toggle-btn {
      padding: 0.5rem 1rem;
      background: white;
      color: $primari;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-weight: bold;
      transition: all 0.2s;

      &:hover {
        background: #f0f0f0;
      }
    }
  }

  .instructions-content {
    padding: 0rem;
    max-height: 0rem;
    overflow: none;
    transition: max-height 0.3s ease-in-out, overflow 0s 0.7s;
    transition: padding 0.3s ease-in-out, overflow 0s 0.7s;

    &.expanded {
      padding: 1.2rem;
      max-height: 9999px !important;
      overflow: visible !important;
      transition: max-height 0.3s ease-in-out, overflow 0s 0.7s;
      transition: padding 0.3s ease-in-out, overflow 0s 0.7s;
    }
  }

}
</style>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { api } from '../utils/axios'
import { getTextColor, markdown } from '../utils/helpers'
import loading from '@/components/loading.vue'
import { useMessagesStore } from '@/stores/messages'
import { useNotificationsStore } from '@/stores/notifications'
import RandomImage from '../components/RandomHome.vue'

const messages = useMessagesStore()
const notificationsStore = useNotificationsStore()
const skills = ref([])
const badges = ref([])
const totalWeight = ref(0)
const completedProjectsCount = ref(0)
const status = ref('Loading your skills...')
const instructions = ref('')
const isInstructionsExpanded = ref(false)

// Vypočítá progress váhy pro badge - celková váha všech dokončených projektů
const getBadgeWeightProgress = (badge) => {
  if (badge.weight_limit === 0) return { current: 0, percentage: 0, met: true }

  const percentage = Math.round((totalWeight.value / badge.weight_limit) * 100)
  return {
    current: totalWeight.value,
    percentage,
    met: totalWeight.value >= badge.weight_limit
  }
}

// Vypočítá progress počtu skillů - celkový počet různých skillů uživatele
const getBadgeSkillsProgress = (badge) => {
  if (badge.skills_count_limit === 0) return { completed: 0, percentage: 0, met: true }

  const totalSkillsCount = skills.value.length
  const percentage = Math.round((totalSkillsCount / badge.skills_count_limit) * 100)

  return {
    completed: totalSkillsCount,
    percentage,
    met: totalSkillsCount >= badge.skills_count_limit
  }
}

// Kontrola splnění konkrétních skillů z badge
const getBadgeSpecificSkillsProgress = (badge) => {
  if (badge.skills.length === 0) return { completed: 0, total: 0, percentage: 0, met: true }

  const userSkillIds = new Set(skills.value.map((s) => s.id))
  const requiredSkills = badge.skills.length
  const completedSkills = badge.skills.filter((skill) => userSkillIds.has(skill.id)).length
  const percentage = Math.round((completedSkills / requiredSkills) * 100)

  return {
    completed: completedSkills,
    total: requiredSkills,
    percentage,
    met: completedSkills >= requiredSkills
  }
}

// Vrátí chybějící skills pro badge (konkrétní skilly)
const getMissingSkills = (badge) => {
  const userSkillIds = new Set(skills.value.map((s) => s.id))
  return badge.skills.filter((skill) => !userSkillIds.has(skill.id))
}

// Vrátí všechny skills z badge s informací, zda je uživatel má
const getAllBadgeSkills = (badge) => {
  const userSkillIds = new Set(skills.value.map((s) => s.id))
  return badge.skills.map((skill) => ({
    ...skill,
    obtained: userSkillIds.has(skill.id)
  }))
}

// Zkontroluje, jestli je badge dokončený podle strategie
const isBadgeCompleted = (badge) => {
  const weightMet = getBadgeWeightProgress(badge).met
  const skillsCountMet = getBadgeSkillsProgress(badge).met
  const specificSkillsMet = getBadgeSpecificSkillsProgress(badge).met

  const conditionsMet = [weightMet, skillsCountMet, specificSkillsMet].filter((x) => x).length

  if (badge.strategy === 'all') {
    return conditionsMet === 3
  } else if (badge.strategy === 'any') {
    return conditionsMet >= 1
  } else if (badge.strategy === 'two') {
    return conditionsMet >= 2
  }

  return false
}

function toggleInstructions() {
  isInstructionsExpanded.value = !isInstructionsExpanded.value
}

onMounted(async () => {
  try {
    const [skillsResponse, badgesResponse, instructionsResponse] = await Promise.all([
      api.get('/user/skills'),
      api.get('/badges'),
      api.get('/instructions'),
      notificationsStore.fetch()
    ])

    skills.value = skillsResponse.data.skills
    totalWeight.value = skillsResponse.data.total_weight
    completedProjectsCount.value = skillsResponse.data.completed_projects_count
    badges.value = badgesResponse.data
    instructions.value = instructionsResponse.data.content

    status.value = null
  } catch (error) {
    status.value = error.response?.data?.msg || 'Error fetching skills data'
    messages.error('Error: ' + (error.response?.data?.msg || error.message))
  }
})
</script>
